import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, Input, Modal, Select, Typography } from 'antd'
import { useState } from 'react'

import {
  actOnImage,
  type CatalogImage,
  type ImageAction,
  IMAGES_KEY,
} from '../api'

const copy: Record<ImageAction, { title: string; description: string }> = {
  publish: {
    title: 'Publish',
    description:
      'The backend checks readiness on every registered cluster. Replacing an entry marks the old entry as removed, stops future preparation attempts, and transfers its default in one operation. Existing disks keep their source.',
  },
  hide: {
    title: 'Hide',
    description:
      'Remove this image from new user choices. Preparation can continue; existing disks and drafts keep their source.',
  },
  default: {
    title: 'Set default',
    description: 'Use this published image as the default for new selections.',
  },
  remove: {
    title: 'Remove',
    description:
      'Hide this entry and stop future preparation attempts. Existing disk sources are retained; an already started preparation can finish.',
  },
}

export function ImageActionModal({
  image,
  action,
  images,
  onClose,
}: {
  image: CatalogImage
  action: ImageAction
  images: CatalogImage[]
  onClose: () => void
}) {
  const [replacement, setReplacement] = useState<string>()
  const [confirmation, setConfirmation] = useState('')
  const cache = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => actOnImage(image.id, action, replacement),
    retry: false,
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: IMAGES_KEY })
      onClose()
    },
  })
  const choices = images.filter(
    (i) =>
      i.id !== image.id &&
      (i.state.status === 'PUBLISHED' || i.state.status === 'DEFAULT'),
  )
  return (
    <Modal
      open
      title={`${copy[action].title}: ${image.name}`}
      okText={copy[action].title}
      okButtonProps={{
        danger: action === 'remove',
        disabled: action === 'remove' && confirmation !== image.name,
      }}
      confirmLoading={mutation.isPending}
      onOk={() => mutation.mutate()}
      onCancel={onClose}
      maskClosable={!mutation.isPending}
      closable={!mutation.isPending}
      cancelButtonProps={{ disabled: mutation.isPending }}
    >
      <Typography.Paragraph>{copy[action].description}</Typography.Paragraph>
      {action === 'publish' && (
        <>
          <Typography.Paragraph>
            Replace published entry (optional)
          </Typography.Paragraph>
          <Select
            aria-label="Replace published entry"
            style={{ width: '100%' }}
            allowClear
            placeholder="Publish without replacement"
            value={replacement}
            disabled={mutation.isPending}
            onChange={setReplacement}
            options={choices.map((i) => ({
              value: i.id,
              label: `${i.distribution} ${i.name}${i.state.status === 'DEFAULT' ? ' (default)' : ''}`,
            }))}
          />
        </>
      )}
      {action === 'remove' && (
        <label>
          Type {image.name} to confirm removal
          <Input
            aria-label="Confirm image name"
            value={confirmation}
            disabled={mutation.isPending}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </label>
      )}
      {mutation.error && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          showIcon
          role="alert"
          message={mutation.error.message}
          description="The operation was not confirmed. Refresh the catalog before retrying if the connection was interrupted."
        />
      )}
    </Modal>
  )
}
